import { defineMessages } from '@alltrails/shared/react-intl';
import FilterPillFactory from 'components/explore/filters/FilterPillFactory';
import NestedPanel from 'components/explore/filters/NestedPanel';
import DropdownGroup from '@alltrails/shared/components/DropdownGroup';
import useFeatures from 'hooks/useFeatures';
import useFiltersContext from 'hooks/useFiltersContext';
import useUser from 'hooks/useUser';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import { Context } from '@alltrails/shared/types/context';
import { FilterViewModel } from 'types/Search';
import SavedFilters from '../filters/SavedFilters';
import SaveFilters from '../filters/SaveFilters';
import * as styles from './styles/styles.module.scss';

export type Props = {
  context: Context;
  enableSavedFilters?: boolean;
  enabledFilters: string[];
};

const FILTER_BAR_STRINGS = defineMessages({
  MORE_FILTERS: { defaultMessage: 'More filters' }
});

const FilterBar = ({ context, enabledFilters, enableSavedFilters }: Props) => {
  const {
    formattedDefaultMessages: { MORE_FILTERS }
  } = useFormatMessage(FILTER_BAR_STRINGS);

  const features = useFeatures();
  const { savedFilterSets, sortedFilters } = useFiltersContext();
  const user = useUser();

  const reducedFilters = sortedFilters.reduce(
    (memo, nextFilter) => {
      if (!enabledFilters.includes(nextFilter.enabledKey)) {
        return memo;
      }

      // The admin modal and nested modal filters are mutually exclusive.
      if (nextFilter.admin) {
        // It's an admin filter, but only render it if it has data.
        if (Object.keys(nextFilter.config).length) {
          memo.adminFilters.push(nextFilter);
        }
      } else if (nextFilter.nested) {
        memo.moreFilters.push(nextFilter);
      } else {
        // Everything else is a top-level filter.
        memo.standardFilters.push(nextFilter);
      }

      return memo;
    },
    { adminFilters: [], moreFilters: [], standardFilters: [] }
  );

  return (
    <DropdownGroup>
      {features.includes('saved_filters') && enableSavedFilters && savedFilterSets.length !== 0 && (
        <SavedFilters context={context} filters={sortedFilters} popperClassName={styles.popper} />
      )}
      {reducedFilters.standardFilters.map((filter: FilterViewModel) => (
        <FilterPillFactory key={filter.key} filter={filter} popperClassName={styles.popper} user={user} />
      ))}
      {reducedFilters.moreFilters.length > 1 && (
        <NestedPanel popperClassName={styles.popper} sortedFilters={reducedFilters.moreFilters} title={MORE_FILTERS} user={user} />
      )}
      {reducedFilters.adminFilters.length > 0 && context.adminUser && (
        <NestedPanel sortedFilters={reducedFilters.adminFilters} popperClassName={styles.popper} title="Admin" user={user} />
      )}
      {features.includes('saved_filters') && enableSavedFilters && <SaveFilters />}
    </DropdownGroup>
  );
};

export default FilterBar;
